/**
 * CurrencyRiskBadge — Phase 36 Batch Y (GMIA Phase 2).
 *
 * Small pill that visualises a 0-100 currency risk score plus a tooltip
 * showing the 4-factor breakdown. Color tone follows the bucket:
 *   Low / Low (Base Currency)  → emerald
 *   Medium                     → amber
 *   High                       → orange
 *   Very High                  → rose
 *
 * The badge is intentionally tiny — fits next to the FX chip in the
 * Deal Detail panel header. Tooltip uses native Radix Tooltip (already
 * available via shadcn) so it's keyboard-accessible.
 *
 * Props
 * ─────
 *   bucket       string  — "Low" | "Medium" | "High" | "Very High" | "Low (Base Currency)"
 *   score        number  — 0..100
 *   factors      object  — { volatility, devaluation_5y, inflation, political_stability_percentile }
 *   currency     string  — e.g. "COP" (rendered in tooltip header)
 *   coverage     string  — "full" | "partial" | "snapshot" | "snapshot-partial" | "missing"
 *   compact      bool    — if true, omits the numeric score (just the bucket)
 */
import { ShieldAlert, Shield, ChevronDown } from "lucide-react";
import { useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import CountryRiskCharts from "./CountryRiskCharts";

const TONE_BY_BUCKET = {
  "Low": "border-emerald-200 bg-emerald-50 text-emerald-800",
  "Low (Base Currency)": "border-emerald-200 bg-emerald-50 text-emerald-800",
  "Medium": "border-amber-200 bg-amber-50 text-amber-800",
  "High": "border-orange-200 bg-orange-50 text-orange-800",
  "Very High": "border-rose-200 bg-rose-50 text-rose-800",
};

function fmt(value, suffix = "", digits = 1) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }
  return `${Number(value).toFixed(digits)}${suffix}`;
}

function FactorRow({ label, value, hint }) {
  return (
    <div className="flex items-center justify-between gap-4 text-xs">
      <span className="text-slate-400">{label}</span>
      <span className="font-medium text-slate-100 tabular-nums">
        {value}
        {hint ? <span className="ml-1 text-slate-500">{hint}</span> : null}
      </span>
    </div>
  );
}

export default function CurrencyRiskBadge({
  bucket,
  score,
  factors,
  currency,
  coverage,
  compact = false,
}) {
  if (!bucket) return null;
  const tone = TONE_BY_BUCKET[bucket] || TONE_BY_BUCKET.Low;
  const Icon = bucket === "Very High" || bucket === "High" ? ShieldAlert : Shield;
  const isBase = bucket === "Low (Base Currency)";

  const label = compact || isBase ? bucket : `Risk: ${bucket}`;

  const pill = (
    <span
      data-testid="currency-risk-badge"
      data-bucket={bucket}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${tone}`}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      <span>{label}</span>
      {!compact && !isBase && score != null ? (
        <span className="text-[10px] font-normal opacity-70 tabular-nums">
          {Math.round(Number(score))}/100
        </span>
      ) : null}
    </span>
  );

  if (isBase) {
    // Base-currency case has nothing useful in the tooltip — no factors.
    return pill;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="cursor-help rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
            data-testid="currency-risk-badge-trigger"
          >
            {pill}
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="end"
          className="w-64 rounded-xl border border-slate-700 bg-slate-900 p-3 text-slate-100 shadow-xl"
          data-testid="currency-risk-tooltip"
        >
          <div className="mb-2 flex items-center justify-between border-b border-slate-700 pb-1.5">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-300">
              {currency || "Currency"} risk
            </span>
            <span className="text-[10px] uppercase tracking-wider text-slate-500">
              {coverage === "snapshot" || coverage === "snapshot-partial"
                ? "snapshot"
                : coverage || ""}
            </span>
          </div>
          <div className="space-y-1.5">
            <FactorRow
              label="Volatility (5y)"
              value={fmt(factors?.volatility, "%")}
              hint="(annualized)"
            />
            <FactorRow
              label="5y devaluation"
              value={fmt(factors?.devaluation_5y, "%")}
              hint="(vs base)"
            />
            <FactorRow
              label="Inflation"
              value={fmt(factors?.inflation, "%")}
            />
            <FactorRow
              label="Political stability"
              value={fmt(factors?.political_stability_percentile, "")}
              hint="pctile"
            />
          </div>
          <div className="mt-2 border-t border-slate-700 pt-1.5 text-[10px] text-slate-400">
            Weighted score: {fmt(score, "/100", 1)}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ── Phase 36 Batch Z — full-detail expandable panel ──────────────────
//
// CurrencyRiskBadgeWithDetail wraps the basic badge and adds a small
// "View detailed risk" chevron below the chip. Clicking it expands to
// show CountryRiskCharts — 4 sparkline charts of the historical risk
// drivers. Mounted next to the FX panel on Deal Detail.
export function CurrencyRiskBadgeWithDetail({
  bucket, score, factors, currency, coverage, userBase = "USD",
}) {
  const [expanded, setExpanded] = useState(false);
  const isBase = bucket === "Low (Base Currency)";
  if (!bucket) return null;
  return (
    <div className="flex flex-col gap-2" data-testid="currency-risk-badge-with-detail">
      <CurrencyRiskBadge
        bucket={bucket}
        score={score}
        factors={factors}
        currency={currency}
        coverage={coverage}
      />
      {!isBase ? (
        <button
          type="button"
          onClick={() => setExpanded((p) => !p)}
          aria-expanded={expanded}
          data-testid="currency-risk-detail-toggle"
          className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 transition-colors hover:text-emerald-700"
        >
          <ChevronDown
            className={`h-3 w-3 transition-transform ${expanded ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
          {expanded ? "Hide detailed risk" : "View detailed risk"}
        </button>
      ) : null}
      {expanded && !isBase ? (
        <div className="mt-1 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
          <CountryRiskCharts currency={currency} userBase={userBase} years={5} />
        </div>
      ) : null}
    </div>
  );
}
